import React from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

interface ChartPoint {
  x: number;
  y: number;
}

interface ChartSeries {
  name: string;
  data: ChartPoint[];
}

interface CustomLineChartProps {
  series: ChartSeries[];
  color?: string;
  height?: number | string;
  width?: number | string;
  maxCpcValue?: number | string | null;
}

const CustomLineChart: React.FC<CustomLineChartProps> = ({
  series,
  color = "#38BDF8",
  height = 70,
  width = "100%",
  maxCpcValue,
}) => {
  const safeMax = Number(maxCpcValue);
  const baseSeries = series[0].data;

  // ----- NORMALISATION -----
  const vals = baseSeries.map((p) => p.y);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;

  const normalized = baseSeries.map((p) => ({
    x: p.x,
    y: ((p.y - min) / range) * 100,
  }));

  // ----- AJOUT D’UN POINT VIRTUEL POUR LE MARKER -----
  let markerIndex = -1;

  if (!isNaN(safeMax)) {
    // interpolation linéaire
    const sorted = [...normalized].sort((a, b) => a.x - b.x);

    let yInterp = sorted[0].y;

    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];

      if (safeMax >= a.x && safeMax <= b.x) {
        const t = (safeMax - a.x) / (b.x - a.x);
        yInterp = a.y + t * (b.y - a.y);
        break;
      }
    }

    // insertion du point fantôme
    normalized.push({
      x: safeMax,
      y: yInterp,
    });

    markerIndex = normalized.length - 1; // dernier point
  }

  const options: ApexOptions = {
    chart: {
      type: "line",
      sparkline: { enabled: true },
      animations: { enabled: false }
    },
    colors: [color],
    stroke: {
      width: 2,
      curve: "smooth",
    },
    markers: {
      size: 0,
      discrete: markerIndex !== -1
        ? [
          {
            seriesIndex: 0,
            dataPointIndex: markerIndex,
            fillColor: "#fff",
            strokeColor: color,
            size: 5,
          },
        ]
        : [],
    },
    tooltip: { enabled: false },
    xaxis: { axisBorder: { show: false }, labels: { show: false } },
    yaxis: { axisBorder: { show: false }, labels: { show: false }, min: 0, max: 100 },
    grid: { show: false },
  };

  return (
    <Chart
      type="line"
      options={options}
      series={[{ name: series[0].name, data: normalized }]}
      height={height}
      width={width}
    />
  );
};

export default CustomLineChart;
